import { useState } from "react";
import { Mic, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nProvider";
import { useDialogs } from "@/lib/dialogs";
import { LANGUAGES } from "@/config/regions";
import { extractPreview } from "@/lib/sarvamDemo";

export const SarvamBand = () => {
  const { lang, setLang } = useI18n();
  const open = useDialogs();
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);

  const runPreview = () => {
    if (!text.trim()) { toast.warning("Enter a sample inquiry first."); return; }
    setPreview(extractPreview(text.trim()));
  };

  const runMic = () => {
    const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Speech) { toast.warning("Browser speech recognition is unavailable. Type an inquiry instead."); return; }
    if (!window.confirm("Use your browser microphone for a local demo? Audio may be processed by your browser's speech service, NOT Sarvam AI. Continue?")) return;
    const sr = new Speech();
    sr.lang = LANGUAGES.find((l) => l.code === lang)?.speech || "en-IN";
    sr.onresult = (e) => { setText(e.results[0][0].transcript); toast.success("Browser transcription ready; review the text before previewing."); };
    sr.onerror = () => toast.error("Speech recognition unavailable; type your inquiry.");
    try { sr.start(); } catch { toast.error("Could not start browser microphone."); }
  };

  const useAsInquiry = () => open("inquiry", { prefill: { type: preview.type || "Wedding", city: preview.city || "", guests: preview.guests || "", notes: text.trim() } });

  return (
    <section className="intel-band" id="sarvam" data-testid="sarvam-section">
      <div className="intel-cell">
        <span className="eyebrow gold-eyebrow">INDIA FIRST · LANGUAGE INCLUSIVE</span>
        <h3>Sarvam AI integration — built around real business value</h3>
        <p>Planned: voice-assisted event inquiries, translation and structured intake routed into the <b>existing CRM</b>. No separate AI CRM or operational database. No commercial Sarvam partnership is implied.</p>
        <div className="intel-row">
          {LANGUAGES.slice(0, 5).map((l) => (
            <button key={l.code} className={`langchip ${lang === l.code ? "selected" : ""}`} onClick={() => setLang(l.code)} data-testid={`lang-chip-${l.code}`}>{l.label}</button>
          ))}
        </div>
      </div>
      <div className="intel-cell">
        <span className="eyebrow gold-eyebrow">TRY A SAFE DEMO</span>
        <h3>Describe an event</h3>
        <textarea className="inteltxt" value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="e.g. I need a wedding planner in Hyderabad for 300 guests" aria-label="Describe an event" data-testid="sarvam-demo-input" />
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
          <button className="btn gold small" onClick={runPreview} data-testid="sarvam-preview-button"><Sparkles className="ic" /> Show structured preview</button>
          <button className="btn clear small" onClick={runMic} data-testid="sarvam-mic-button"><Mic className="ic" /> Browser mic</button>
        </div>
        <div className="intel-note">Local demonstration only. No Sarvam API calls or backend writes. Browser speech recognition availability varies.</div>
      </div>
      <div className="intel-cell">
        <span className="eyebrow gold-eyebrow">MEASURABLE VALUE</span>
        <h3>What will be measured?</h3>
        <p>Lead completion rate · intake time saved · translation correction rate · cost per processed inquiry.</p>
        <div className="intel-preview" data-testid="sarvam-structured-preview">
          {!preview ? "Enter an inquiry to preview the proposed structured output." : (
            <>
              <div className="intel-preview-h">Rule-based preview (NOT Sarvam AI)</div>
              Event: {preview.type || "Needs confirmation"}<br />City: {preview.city || "Needs confirmation"}<br />Guests: {preview.guests || "Needs confirmation"}
              <button className="btn gold small" style={{ marginTop: 9, display: "flex", width: "fit-content" }} onClick={useAsInquiry} data-testid="sarvam-use-as-inquiry-button">Review as inquiry <ArrowRight className="ic" /></button>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
